﻿import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next, useTranslation } from "react-i18next";
import { z } from "zod";

import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocaleDir,
  type Locale,
} from "@ticketnjoy/types";

import en from "@/messages/en.json";
import tr from "@/messages/tr.json";
import ar from "@/messages/ar.json";

export const LOCALE_COOKIE_NAME = "NEXT_LOCALE";

// Cookie + Accept-Language üstünden locale çözüyoruz (URL prefix YOK).
// Mevcut backend X-Locale header'ı NEXT_LOCALE cookie'sini bekliyor → isim
// korunmalı.
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      tr: { translation: tr },
      ar: { translation: ar },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    interpolation: { escapeValue: false },
    detection: {
      order: ["cookie", "navigator", "htmlTag"],
      caches: ["cookie"],
      lookupCookie: LOCALE_COOKIE_NAME,
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: "/", sameSite: "lax" },
    },
    react: {
      useSuspense: false,
    },
  });

// `<html lang>` / `<html dir>` ve mesajı yazılmamış zod kurallarının dili
// her dil değişiminde güncellensin.
function applyLocale(locale: Locale) {
  document.documentElement.setAttribute("lang", locale);
  document.documentElement.setAttribute("dir", getLocaleDir(locale));
  z.config(z.locales[locale]());
}

applyLocale((i18n.resolvedLanguage as Locale) ?? DEFAULT_LOCALE);
i18n.on("languageChanged", (lng) => {
  if ((SUPPORTED_LOCALES as readonly string[]).includes(lng)) {
    applyLocale(lng as Locale);
  }
});

export default i18n;

// next-intl'deki `useT(namespace)` API'sine yakın bir wrapper:
// `t("auth.login")` veya `useT("auth")` + `t("login")` ikisi de çalışsın.
// Mevcut çağrılar (ör. `useT("locale")` + `t("switcher")`) tek
// namespace altında flat key olduğundan ns prefix'iyle birleştiriyoruz.
//
// Dönüş tipi `string` — react-i18next default'ta TFunctionDetailedResult dönüp
// JSX child olarak kabul ettirmiyor; mesaj kataloglarımız flat string olduğu
// için return'ü string olarak daraltıyoruz.
export type TFunction = ((key: string, params?: Record<string, unknown>) => string) & {
  i18n: ReturnType<typeof useTranslation>["i18n"];
};

export function useT(namespace?: string): TFunction {
  const { t, i18n: instance } = useTranslation();
  const fn = (key: string, params?: Record<string, unknown>) =>
    t(namespace ? `${namespace}.${key}` : key, params as never) as unknown as string;
  return Object.assign(fn, { i18n: instance }) as TFunction;
}

// next-intl'deki `useLocale()` ile eşdeğer.
export function useLocale(): Locale {
  const { i18n: instance } = useTranslation();
  return ((instance.resolvedLanguage as Locale) ?? DEFAULT_LOCALE);
}
